import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { resolveServiceSteps } from "../pages/smrtdocsServices";

// ─── Notification type + localStorage helpers ────────────────────────────────

const STORAGE_KEY = "smrtdocs_notifications";

export type SMRTDocsNotification = {
  id: string;
  userId: string;
  docKey: string;
  type: "red" | "yellow" | "green";
  message: string;
  timestamp: number;
  read: boolean;
};

export function getNotifications(): SMRTDocsNotification[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : [];
}

export function saveNotifications(list: SMRTDocsNotification[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

export function addNotification(notification: SMRTDocsNotification): void {
  const list = getNotifications();
  list.push(notification);
  saveNotifications(list);
}

export function markAsRead(id: string): void {
  const list = getNotifications().map((n) =>
    n.id === id ? { ...n, read: true } : n,
  );
  saveNotifications(list);
}

// ─── Merge helper ────────────────────────────────────────────────────────────

// Local edits (admin "Local Changes") are scoped per service so an addition
// made on one of a user's assigned services never bleeds onto another. They are
// stored under localDocs.byService keyed by the service index (the same index
// used by assignments / viewServiceIndex). Returns an empty slice when there is
// no entry for the given service — including legacy flat localDocs that predate
// the per-service split (those are intentionally not applied to any service).
export function getServiceLocalDocs(
  localDocs: any,
  serviceKey?: string | number,
): { attachedFields: Record<string, any[]>; steps: any[] } {
  const empty = { attachedFields: {}, steps: [] };
  if (!localDocs?.byService || serviceKey === undefined || serviceKey === null) {
    return empty;
  }
  return localDocs.byService[String(serviceKey)] || empty;
}

export function getMergedSteps(
  steps: any[],
  userId: string,
  localDocs?: any,
  serviceKey?: string | number,
): any[] {
  const ld = getServiceLocalDocs(localDocs, serviceKey);
  const merged = steps.map((step: any) => {
    const attached = ld.attachedFields?.[step.id] || [];
    return attached.length > 0 ? { ...step, fields: [...(step.fields || []), ...attached] } : step;
  });
  return [...merged, ...(ld.steps || [])];
}

// ─── Component ───────────────────────────────────────────────────────────────

export type UserState = {
  files: Record<string, any>;
  issued: Record<string, any>;
  approved: Record<string, boolean>;
  notified: Record<string, boolean>;
  history: Record<string, any[]>;
  reminders: Record<string, any>;
  localDocs: any;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  filter: "red" | "yellow" | "green" | null;
  services: any[];
  assignments: Record<string, any>;
  users: any[];
  userStates: Record<string, UserState>;
  overrides: Record<string, any>;
};

const TITLES: Record<string, string> = {
  red: "Needs Attention",
  yellow: "Overdue",
  green: "Unread",
};

type RedItem = {
  userId: string;
  userName: string;
  docLabel: string;
  status: string;
};

export function getRedItems(services: any[] = [], assignments: Record<string, any> = {}, users: any[] = [], userStates: Record<string, UserState> = {}, overrides: Record<string, any> = {}): RedItem[] {

  const items: RedItem[] = [];

  // Check for users with no valid service assignment
  for (const u of users) {
    const uid = u.user_id || u.id;
    if (!uid) continue;
    const uName = u.name || u.email || uid;
    const a = assignments[uid];

    let hasValid = false;
    if (a?.mode === "single") {
      const v = a.value;
      hasValid = v != null && v !== "" && v !== "- Please Select -" && services[v] != null;
    } else if (a?.mode === "multi") {
      hasValid = Array.isArray(a.values) && a.values.some((v: any) => services[v] != null);
    }

    if (!hasValid) {
      items.push({ userId: uid, userName: uName, docLabel: "No services assigned", status: "Unassigned" });
    }
  }

  for (const [userId, assignment] of Object.entries(assignments)) {
    const user = users.find((u: any) => u.user_id === userId || u.id === userId);
    const userName = user?.name || user?.email || userId;
    const us = userStates[userId] || { files: {}, issued: {}, approved: {}, notified: {}, localDocs: { attachedFields: {}, steps: [] } };
    const files = us.files || {};
    const issued = us.issued || {};
    const approved = us.approved || {};
    const notifiedMap = us.notified || {};

    // Optional-chained: a user can be present in the map with no assignment
    // under them, and reading .mode off that is a crash rather than an empty
    // list. Defaulted too, since "multi" with no values array is a shape the
    // screen can write.
    const serviceIndices: number[] =
      (assignment as any)?.mode === "single"
        ? [(assignment as any).value]
        : (assignment as any)?.mode === "multi"
          ? (assignment as any).values || []
          : [];

    let hasUnsent = false;

    for (const idx of serviceIndices) {
      const svc = services[idx];
      // Resolved, not raw: a Sub Service's lines live on its Master.
      const serviceSteps = resolveServiceSteps(services, svc);
      if (serviceSteps.length === 0) continue;

      const mergedSteps = getMergedSteps(serviceSteps, userId, us.localDocs, idx);
      for (const step of mergedSteps) {
        for (const field of step.fields || []) {
          const override = overrides[`${userId}_${field.key}`] || {};
          if (override.hidden) continue;

          const approvalRequired = override.approvalRequired !== undefined ? override.approvalRequired : field.approvalRequired;

          // Check for unsent notifications
          if (notifiedMap[field.key] === false && issued[field.key]) {
            hasUnsent = true;
          }

          // Compute status
          let status = "";
          if (field.type === "certificate") {
            if (!files[field.key]) status = "Pending";
            else if (!issued[field.key]) status = "Draft";
            else if (approvalRequired && !approved[field.key]) status = "Client Submitted";
          } else if (field.type === "template") {
            if (!files[field.key]) status = "Pending";
            else if (!issued[field.key]) status = "Draft";
            else if (typeof issued[field.key] === "object") {
              if (issued[field.key].status === "Client Submitted" && approvalRequired) status = "Client Submitted";
            }
          } else if (field.type === "info") {
            if (!files[field.key] && !issued[field.key]) status = "Pending";
            else if (files[field.key] && !issued[field.key]) status = "Draft";
          }

          // RED: Client Submitted (any type) OR Pending/Draft for admin-owned (info, template)
          if (status === "Client Submitted") {
            items.push({ userId, userName, docLabel: field.label, status });
          } else if ((status === "Pending" || status === "Draft") && field.type !== "certificate") {
            items.push({ userId, userName, docLabel: field.label, status });
          }
        }
      }
    }

    if (hasUnsent) {
      items.push({ userId, userName, docLabel: "You have unsent notices for this User", status: "Unsent" });
    }
  }

  return items;
}

type YellowItem = {
  userId: string;
  userName: string;
  docLabel: string;
  status: string;
};

export function getYellowItems(services: any[] = [], assignments: Record<string, any> = {}, users: any[] = [], userStates: Record<string, UserState> = {}, overrides: Record<string, any> = {}): YellowItem[] {

  const items: YellowItem[] = [];

  for (const [userId, assignment] of Object.entries(assignments)) {
    const user = users.find((u: any) => u.user_id === userId || u.id === userId);
    const userName = user?.name || user?.email || userId;
    const us = userStates[userId] || { files: {}, issued: {}, localDocs: { attachedFields: {}, steps: [] } };
    const files = us.files || {};
    const issued = us.issued || {};

    // Optional-chained: a user can be present in the map with no assignment
    // under them, and reading .mode off that is a crash rather than an empty
    // list. Defaulted too, since "multi" with no values array is a shape the
    // screen can write.
    const serviceIndices: number[] =
      (assignment as any)?.mode === "single"
        ? [(assignment as any).value]
        : (assignment as any)?.mode === "multi"
          ? (assignment as any).values || []
          : [];

    for (const idx of serviceIndices) {
      const svc = services[idx];
      // Resolved, not raw: a Sub Service's lines live on its Master.
      const serviceSteps = resolveServiceSteps(services, svc);
      if (serviceSteps.length === 0) continue;

      const mergedSteps = getMergedSteps(serviceSteps, userId, us.localDocs, idx);
      for (const step of mergedSteps) {
        for (const field of step.fields || []) {
          // Skip hidden fields
          if (overrides[`${userId}_${field.key}`]?.hidden) continue;

          // YELLOW: only certificates with Pending or Draft
          if (field.type !== "certificate") continue;

          let status = "";
          if (!files[field.key]) status = "Pending";
          else if (!issued[field.key]) status = "Draft";

          if (status === "Pending" || status === "Draft") {
            items.push({ userId, userName, docLabel: field.label, status });
          }
        }
      }
    }
  }

  return items;
}

type GreenItem = {
  id: string;
  userId: string;
  userName: string;
  docLabel: string;
  status: string;
};

export function getGreenItems(services: any[] = [], assignments: Record<string, any> = {}, users: any[] = [], userStates: Record<string, UserState> = {}, overrides: Record<string, any> = {}): GreenItem[] {

  const items: GreenItem[] = [];

  for (const [userId, assignment] of Object.entries(assignments)) {
    const user = users.find((u: any) => u.user_id === userId || u.id === userId);
    const userName = user?.name || user?.email || userId;
    const us = userStates[userId] || { files: {}, issued: {}, localDocs: { attachedFields: {}, steps: [] } };
    const files = us.files || {};
    const issued = us.issued || {};

    // Optional-chained: a user can be present in the map with no assignment
    // under them, and reading .mode off that is a crash rather than an empty
    // list. Defaulted too, since "multi" with no values array is a shape the
    // screen can write.
    const serviceIndices: number[] =
      (assignment as any)?.mode === "single"
        ? [(assignment as any).value]
        : (assignment as any)?.mode === "multi"
          ? (assignment as any).values || []
          : [];

    for (const idx of serviceIndices) {
      const svc = services[idx];
      // Resolved, not raw: a Sub Service's lines live on its Master.
      const serviceSteps = resolveServiceSteps(services, svc);
      if (serviceSteps.length === 0) continue;

      const mergedSteps = getMergedSteps(serviceSteps, userId, us.localDocs, idx);
      for (const step of mergedSteps) {
        for (const field of step.fields || []) {
          if (overrides[`${userId}_${field.key}`]?.hidden) continue;
          if (field.type !== "certificate") continue;

          const override = overrides[`${userId}_${field.key}`] || {};
          const approvalRequired = override.approvalRequired !== undefined ? override.approvalRequired : field.approvalRequired;

          // GREEN = issued, no approval required (admin has never reviewed)
          const isUnread =
            !!files[field.key] &&
            !!issued[field.key] &&
            !approvalRequired;

          if (isUnread) {
            const id = `${userId}_${field.key}`;
            items.push({ id, userId, userName, docLabel: field.label, status: "Active" });
          }
        }
      }
    }
  }

  // Filter out items already marked as read
  const readSet = new Set(getNotifications().filter((n) => n.read).map((n) => n.id));
  return items.filter((item) => !readSet.has(item.id));
}

type GroupedUser = {
  userId: string;
  userName: string;
  items: { id?: string; docLabel: string; status: string }[];
};

function groupByUser(items: { id?: string; userId: string; userName: string; docLabel: string; status: string }[]): GroupedUser[] {
  const map: Record<string, GroupedUser> = {};
  for (const item of items) {
    if (!map[item.userId]) {
      map[item.userId] = { userId: item.userId, userName: item.userName, items: [] };
    }
    map[item.userId].items.push({ id: item.id, docLabel: item.docLabel, status: item.status });
  }
  return Object.values(map);
}

const STATUS_COLORS: Record<string, string> = {
  red: "#c62828",
  yellow: "#d4a017",
  green: "#1faa59",
};

export default function SMRTDocsTrafficModal({ isOpen, onClose, filter, services, assignments, users, userStates, overrides }: Props) {
  const navigate = useNavigate();
  const [expandedUsers, setExpandedUsers] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [, setRefresh] = useState(0);

  // Force recalculation when modal opens or filter changes
  React.useEffect(() => {
    if (isOpen) {
      setRefresh((n) => n + 1);
      setSelectedIds([]);
    }
  }, [isOpen, filter]);

  if (!isOpen) return null;

  const title = filter ? TITLES[filter] : "All Notifications";
  const rawItems =
    filter === "red" ? getRedItems(services, assignments, users, userStates, overrides) :
    filter === "yellow" ? getYellowItems(services, assignments, users, userStates, overrides) :
    filter === "green" ? getGreenItems(services, assignments, users, userStates, overrides) :
    [];

  const groups = groupByUser(rawItems);
  const statusColor = STATUS_COLORS[filter || "red"] || "#9aa0a6";
  const isGreen = filter === "green";

  // Collect all green item IDs for select-all
  const allGreenIds: string[] = isGreen
    ? groups.flatMap((g) => g.items.filter((i) => i.id).map((i) => i.id!))
    : [];
  const allSelected = isGreen && allGreenIds.length > 0 && allGreenIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : [...allGreenIds]);
  };

  const handleMarkAsRead = () => {
    for (const id of selectedIds) {
      // Add as read notification
      addNotification({
        id,
        userId: "",
        docKey: id,
        type: "green",
        message: "Marked as read",
        timestamp: Date.now(),
        read: true,
      });
    }
    setSelectedIds([]);
    setRefresh((n) => n + 1);
  };

  const handleViewAndMark = (group: GroupedUser) => {
    // Mark all green items for this user as read
    if (isGreen) {
      for (const item of group.items) {
        if (item.id) {
          addNotification({
            id: item.id,
            userId: group.userId,
            docKey: item.id,
            type: "green",
            message: "Marked as read",
            timestamp: Date.now(),
            read: true,
          });
        }
      }
    }
    onClose();
    navigate(`/manage-smrt-docs/view/${encodeURIComponent(group.userId)}`);
  };

  const toggleExpand = (userId: string) => {
    setExpandedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "550px", maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>{title}</h2>
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

        {/* Green toolbar: Select All + Mark as Read */}
        {isGreen && groups.length > 0 && (
          <div style={{ padding: "8px 20px", borderBottom: "1px solid #555", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#9aa0a6", fontSize: "0.8rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                style={{ accentColor: "#1faa59" }}
              />
              Select All
            </label>
            {selectedIds.length > 0 && (
              <button
                className="btn-icon"
                style={{ background: "#1faa59", border: "none", borderRadius: 6, color: "#fff", padding: "6px 20px", fontSize: "0.82rem", cursor: "pointer", whiteSpace: "nowrap", width: "auto", minWidth: 130 }}
                onClick={handleMarkAsRead}
              >
                Mark As Read
              </button>
            )}
          </div>
        )}

        <div className="smrtdocs-modal-body" style={{ overflowY: "auto", flex: 1 }}>
          {groups.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {groups.map((group) => (
                <div key={group.userId}>
                  {/* User row */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#1c1c1c",
                      border: "1px solid #2a2a2a",
                      borderRadius: 6,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#fff", fontSize: "0.95rem" }}>{group.userName}</div>
                      <div style={{ fontSize: "0.8rem", color: statusColor }}>
                        {group.items.length} item{group.items.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        className="btn-icon"
                        style={{ width: 32, height: 32, fontSize: 12, background: "#4b5563", border: "1px solid #6b7280", borderRadius: 6, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        title={expandedUsers.includes(group.userId) ? "Collapse" : "Expand"}
                        onClick={() => toggleExpand(group.userId)}
                      >
                        {expandedUsers.includes(group.userId) ? "▶" : "▼"}
                      </button>
                      <button
                        className="btn-icon smrtdocs-btn-view"
                        style={{ width: 32, height: 32, fontSize: 14, flexShrink: 0 }}
                        title="View"
                        onClick={() => handleViewAndMark(group)}
                      >
                        👁
                      </button>
                    </div>
                  </div>

                  {/* Expanded items */}
                  {expandedUsers.includes(group.userId) && (
                    <div style={{ paddingLeft: 12, paddingTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      {group.items.map((item, j) => (
                        <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.88rem", color: "#9aa0a6" }}>
                          {isGreen && item.id && (
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item.id)}
                              onChange={() => toggleSelect(item.id!)}
                              style={{ accentColor: "#1faa59", cursor: "pointer" }}
                            />
                          )}
                          {filter === "red"
                            ? item.status === "Unassigned"
                              ? "Service not assigned"
                              : item.status === "Unsent"
                                ? item.docLabel
                                : item.status === "Client Submitted"
                                  ? `Please review ${item.docLabel}`
                                  : `Please upload ${item.docLabel}`
                            : filter === "yellow"
                              ? item.status === "Draft"
                                ? `Waiting for ${item.docLabel} to be started`
                                : `Waiting for ${item.docLabel} to be provided`
                              : `${item.docLabel} is ready for review`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#9aa0a6", textAlign: "center", padding: "20px 0" }}>
              No items
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
